import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, Save, Plus, Trash2, ArrowUp, ArrowDown, GripVertical, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { slugify } from '@/lib/utils';

const SECTION_TYPES = [
    { value: 'hero', label: 'Hero' },
    { value: 'page_header', label: 'Header stranice' },
    { value: 'intro', label: 'Tekst + slika' },
    { value: 'services_grid', label: 'Grid usluga' },
    { value: 'why_us', label: 'Značajke' },
    { value: 'references', label: 'Reference' },
    { value: 'process', label: 'Proces (koraci)' },
    { value: 'faq', label: 'FAQ' },
    { value: 'cta', label: 'CTA blok' },
    { value: 'blog_preview', label: 'Blog preview' },
    { value: 'gallery', label: 'Galerija' },
    { value: 'trust', label: 'Trust signali' },
    { value: 'contact_form', label: 'Kontakt forma' },
    { value: 'custom_html', label: 'Custom HTML' },
];

export default function AdminPageEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const isNew = id === 'new';
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [page, setPage] = useState({
        slug: '', title: '', h1: '', description: '', featured_image: '',
        sections: [], seo: defaultSEO(), status: 'draft', page_type: 'custom',
        custom_css: '', custom_js: ''
    });

    function defaultSEO() {
        return {
            meta_title: '', meta_description: '', canonical_url: '',
            robots_index: true, robots_follow: true,
            og_title: '', og_description: '', og_image: '',
            twitter_card: 'summary_large_image',
            schema_json: '',
            include_in_sitemap: true,
            sitemap_priority: 0.7, sitemap_changefreq: 'weekly'
        };
    }

    useEffect(() => {
        if (isNew) {
            setLoading(false);
            return;
        }
        api.get(`/admin/pages/${id}`).then(({ data }) => {
            if (!data.seo) data.seo = defaultSEO();
            setPage(data);
        }).catch(() => toast.error('Stranica nije pronađena')).finally(() => setLoading(false));
    }, [id, isNew]);

    const set = (k, v) => setPage((p) => ({ ...p, [k]: v }));
    const setSeo = (k, v) => setPage((p) => ({ ...p, seo: { ...(p.seo || defaultSEO()), [k]: v } }));

    const onSave = async () => {
        setSaving(true);
        try {
            const payload = { ...page };
            if (isNew) {
                if (!payload.slug) payload.slug = slugify(payload.title);
                const { data } = await api.post('/admin/pages', payload);
                toast.success('Stranica kreirana');
                navigate(`/admin/pages/${data.id}`);
            } else {
                await api.put(`/admin/pages/${id}`, payload);
                toast.success('Promjene spremljene');
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Spremanje nije uspjelo');
        } finally {
            setSaving(false);
        }
    };

    const addSection = () => {
        const newSec = {
            id: `tmp-${Date.now()}`,
            type: 'intro',
            order: (page.sections || []).length + 1,
            enabled: true,
            data: {},
        };
        setPage((p) => ({ ...p, sections: [...(p.sections || []), newSec] }));
    };

    const updateSection = (idx, updates) => {
        setPage((p) => {
            const sec = [...p.sections];
            sec[idx] = { ...sec[idx], ...updates };
            return { ...p, sections: sec };
        });
    };

    const updateSectionData = (idx, dataUpdates) => {
        setPage((p) => {
            const sec = [...p.sections];
            sec[idx] = { ...sec[idx], data: { ...(sec[idx].data || {}), ...dataUpdates } };
            return { ...p, sections: sec };
        });
    };

    const removeSection = (idx) => {
        if (!confirm('Obrisati sekciju?')) return;
        setPage((p) => ({ ...p, sections: p.sections.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })) }));
    };

    const moveSection = (idx, dir) => {
        setPage((p) => {
            const sec = [...p.sections];
            const target = idx + dir;
            if (target < 0 || target >= sec.length) return p;
            [sec[idx], sec[target]] = [sec[target], sec[idx]];
            return { ...p, sections: sec.map((s, i) => ({ ...s, order: i + 1 })) };
        });
    };

    if (loading) return <div className="text-white/60">Učitavanje…</div>;

    return (
        <div className="max-w-5xl">
            <Link to="/admin/pages" className="inline-flex items-center text-sm text-white/70 hover:text-white mb-4"><ArrowLeft className="mr-2 h-4 w-4" /> Sve stranice</Link>
            <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                    <h1 className="text-2xl font-medium">{isNew ? 'Nova stranica' : page.title}</h1>
                    {!isNew && <p className="mt-1 text-sm text-white/60 font-mono">/{page.slug === 'home' ? '' : page.slug}</p>}
                </div>
                <Button onClick={onSave} disabled={saving} className="bg-[#B08D57] hover:bg-[#9c7d4e] text-white" data-testid="admin-save-button">
                    {saving ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Spremanje…</> : <><Save className="mr-2 h-4 w-4" /> Spremi</>}
                </Button>
            </div>

            <Tabs defaultValue="content" className="mt-6">
                <TabsList className="bg-[#161A21]">
                    <TabsTrigger value="content" data-testid="tab-content">Sadržaj</TabsTrigger>
                    <TabsTrigger value="sections" data-testid="tab-sections">Sekcije ({page.sections?.length || 0})</TabsTrigger>
                    <TabsTrigger value="seo" data-testid="tab-seo">SEO</TabsTrigger>
                    <TabsTrigger value="advanced" data-testid="tab-advanced">Napredno</TabsTrigger>
                </TabsList>

                <TabsContent value="content" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-4">
                        <div>
                            <Label className="text-white">Naslov stranice</Label>
                            <Input value={page.title || ''} onChange={(e) => set('title', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white" data-testid="page-title-input" />
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <Label className="text-white">Slug (URL)</Label>
                                <Input value={page.slug || ''} onChange={(e) => set('slug', slugify(e.target.value))} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white font-mono" data-testid="page-slug-input" />
                            </div>
                            <div>
                                <Label className="text-white">Tip stranice</Label>
                                <Select value={page.page_type || 'custom'} onValueChange={(v) => set('page_type', v)}>
                                    <SelectTrigger className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white"><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="custom">Custom</SelectItem>
                                        <SelectItem value="home">Home</SelectItem>
                                        <SelectItem value="service">Service</SelectItem>
                                        <SelectItem value="about">About</SelectItem>
                                        <SelectItem value="contact">Contact</SelectItem>
                                        <SelectItem value="blog_list">Blog List</SelectItem>
                                        <SelectItem value="references_list">References List</SelectItem>
                                        <SelectItem value="services_list">Services List</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        <div>
                            <Label className="text-white">H1 (glavni naslov na stranici)</Label>
                            <Input value={page.h1 || ''} onChange={(e) => set('h1', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white" />
                        </div>
                        <div>
                            <Label className="text-white">Kratak opis</Label>
                            <Textarea value={page.description || ''} onChange={(e) => set('description', e.target.value)} rows={3} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white" />
                        </div>
                        <div>
                            <Label className="text-white">Featured slika (URL)</Label>
                            <Input value={page.featured_image || ''} onChange={(e) => set('featured_image', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white" placeholder="https://…" />
                        </div>
                        <div className="flex items-center gap-3">
                            <Switch checked={page.status === 'published'} onCheckedChange={(v) => set('status', v ? 'published' : 'draft')} />
                            <Label className="text-white">Objavljeno</Label>
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="sections" className="mt-6">
                    <div className="space-y-3">
                        {(page.sections || []).map((sec, idx) => (
                            <Card key={sec.id || idx} className="bg-[#111318] border-[#232A36] p-4">
                                <div className="flex items-center justify-between flex-wrap gap-3">
                                    <div className="flex items-center gap-2">
                                        <GripVertical className="h-4 w-4 text-white/40" />
                                        <Badge variant="outline" className="border-[#B08D57] text-[#B08D57]">#{idx + 1}</Badge>
                                        <Select value={sec.type} onValueChange={(v) => updateSection(idx, { type: v })}>
                                            <SelectTrigger className="w-56 bg-[#0B0C0E] border-[#232A36] text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {SECTION_TYPES.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                                            </SelectContent>
                                        </Select>
                                        <div className="flex items-center gap-2">
                                            <Switch checked={sec.enabled !== false} onCheckedChange={(v) => updateSection(idx, { enabled: v })} />
                                            <span className="text-xs text-white/60">{sec.enabled !== false ? 'Aktivno' : 'Skriveno'}</span>
                                        </div>
                                    </div>
                                    <div className="flex gap-1">
                                        <Button size="icon" variant="outline" onClick={() => moveSection(idx, -1)} className="h-8 w-8 border-[#232A36] text-white hover:bg-[#232A36]"><ArrowUp className="h-3.5 w-3.5" /></Button>
                                        <Button size="icon" variant="outline" onClick={() => moveSection(idx, 1)} className="h-8 w-8 border-[#232A36] text-white hover:bg-[#232A36]"><ArrowDown className="h-3.5 w-3.5" /></Button>
                                        <Button size="icon" variant="outline" onClick={() => removeSection(idx)} className="h-8 w-8 border-red-900 text-red-400 hover:bg-red-900/20"><Trash2 className="h-3.5 w-3.5" /></Button>
                                    </div>
                                </div>
                                <SectionDataEditor section={sec} onChange={(data) => updateSectionData(idx, data)} onCssChange={(v) => updateSection(idx, { custom_css: v })} />
                            </Card>
                        ))}
                        <Button onClick={addSection} variant="outline" className="border-[#232A36] text-white hover:bg-[#232A36]"><Plus className="mr-2 h-4 w-4" /> Dodaj sekciju</Button>
                    </div>
                </TabsContent>

                <TabsContent value="seo" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-4">
                        <div>
                            <Label className="text-white">SEO Title</Label>
                            <Input value={page.seo?.meta_title || ''} onChange={(e) => setSeo('meta_title', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white" />
                        </div>
                        <div>
                            <Label className="text-white">Meta Description</Label>
                            <Textarea value={page.seo?.meta_description || ''} onChange={(e) => setSeo('meta_description', e.target.value)} rows={3} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white" />
                            <p className="text-xs text-white/50 mt-1">{(page.seo?.meta_description || '').length} / 160 znakova</p>
                        </div>
                        <div>
                            <Label className="text-white">Canonical URL</Label>
                            <Input value={page.seo?.canonical_url || ''} onChange={(e) => setSeo('canonical_url', e.target.value)} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white" placeholder="https://skobic-keramika.hr/…" />
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                            <div className="flex items-center gap-2">
                                <Switch checked={page.seo?.robots_index !== false} onCheckedChange={(v) => setSeo('robots_index', v)} />
                                <Label className="text-white">Index</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={page.seo?.robots_follow !== false} onCheckedChange={(v) => setSeo('robots_follow', v)} />
                                <Label className="text-white">Follow</Label>
                            </div>
                            <div className="flex items-center gap-2">
                                <Switch checked={page.seo?.include_in_sitemap !== false} onCheckedChange={(v) => setSeo('include_in_sitemap', v)} />
                                <Label className="text-white">U sitemap.xml</Label>
                            </div>
                        </div>
                        <div className="pt-4 border-t border-[#232A36]">
                            <p className="text-xs uppercase tracking-[0.18em] text-white/60 mb-3">Open Graph (društvene mreže)</p>
                            <div className="space-y-3">
                                <div>
                                    <Label className="text-white text-xs">OG Title</Label>
                                    <Input value={page.seo?.og_title || ''} onChange={(e) => setSeo('og_title', e.target.value)} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                                </div>
                                <div>
                                    <Label className="text-white text-xs">OG Description</Label>
                                    <Textarea value={page.seo?.og_description || ''} onChange={(e) => setSeo('og_description', e.target.value)} rows={2} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                                </div>
                                <div>
                                    <Label className="text-white text-xs">OG Image URL</Label>
                                    <Input value={page.seo?.og_image || ''} onChange={(e) => setSeo('og_image', e.target.value)} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                                </div>
                            </div>
                        </div>
                        <div>
                            <Label className="text-white">Custom Schema JSON-LD</Label>
                            <Textarea value={page.seo?.schema_json || ''} onChange={(e) => setSeo('schema_json', e.target.value)} rows={4} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white font-mono text-xs" placeholder='{"@context":"https://schema.org",...}' />
                        </div>
                    </Card>
                </TabsContent>

                <TabsContent value="advanced" className="mt-6">
                    <Card className="bg-[#111318] border-[#232A36] p-6 space-y-4">
                        <p className="text-xs text-white/60"><strong>Pažnja:</strong> Custom CSS i JS mogu utjecati na funkcionalnost i SEO. Koristite samo ako znate što radite.</p>
                        <div>
                            <Label className="text-white">Custom CSS (za ovu stranicu)</Label>
                            <Textarea value={page.custom_css || ''} onChange={(e) => set('custom_css', e.target.value)} rows={8} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white font-mono text-xs" placeholder=".my-class { color: red; }" />
                        </div>
                        <div>
                            <Label className="text-white">Custom JS (samo admin pregled)</Label>
                            <Textarea value={page.custom_js || ''} onChange={(e) => set('custom_js', e.target.value)} rows={6} className="mt-2 bg-[#0B0C0E] border-[#232A36] text-white font-mono text-xs" placeholder="// JS code" />
                            <p className="text-xs text-yellow-400/80 mt-1">JS se ne izvodi na javnom prikazu – samo čuvanje u CMS-u za referencu.</p>
                        </div>
                    </Card>
                </TabsContent>
            </Tabs>
        </div>
    );
}

// Inline section data editor
function SectionDataEditor({ section, onChange, onCssChange }) {
    const d = section.data || {};
    return (
        <div className="mt-4 pt-4 border-t border-[#232A36] space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                    <Label className="text-white text-xs">Eyebrow (mali tekst iznad)</Label>
                    <Input value={d.eyebrow || ''} onChange={(e) => onChange({ eyebrow: e.target.value })} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                </div>
                <div>
                    <Label className="text-white text-xs">Naslov</Label>
                    <Input value={d.heading || ''} onChange={(e) => onChange({ heading: e.target.value })} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                </div>
            </div>
            <div>
                <Label className="text-white text-xs">Podnaslov / Tekst</Label>
                <Textarea value={d.subheading || d.text || ''} onChange={(e) => onChange(section.type === 'intro' ? { text: e.target.value } : { subheading: e.target.value })} rows={3} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
            </div>
            {(section.type === 'hero' || section.type === 'page_header' || section.type === 'cta') && (
                <>
                    <div>
                        <Label className="text-white text-xs">Pozadinska slika (URL)</Label>
                        <Input value={d.background_image || ''} onChange={(e) => onChange({ background_image: e.target.value })} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">Alt tekst</Label>
                        <Input value={d.image_alt || ''} onChange={(e) => onChange({ image_alt: e.target.value })} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                    </div>
                </>
            )}
            {section.type === 'intro' && (
                <>
                    <div>
                        <Label className="text-white text-xs">Slika (URL)</Label>
                        <Input value={d.image || ''} onChange={(e) => onChange({ image: e.target.value })} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">Alt tekst</Label>
                        <Input value={d.image_alt || ''} onChange={(e) => onChange({ image_alt: e.target.value })} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white" />
                    </div>
                </>
            )}
            {section.type === 'custom_html' && (
                <div>
                    <Label className="text-white text-xs">HTML (samo admin upravlja)</Label>
                    <Textarea value={d.html || ''} onChange={(e) => onChange({ html: e.target.value })} rows={6} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white font-mono text-xs" />
                </div>
            )}
            {/* Typed array editors with + Add / Delete buttons */}
            {section.type === 'gallery' && (
                <ArrayEditor
                    label="Slike"
                    items={Array.isArray(d.images) ? d.images : []}
                    onChange={(items) => onChange({ images: items })}
                    blank={{ url: '', alt: '' }}
                    fields={[
                        { key: 'url', label: 'URL slike *', required: true, placeholder: 'https://… ili /api/media/…' },
                        { key: 'alt', label: 'Alt tekst', required: false },
                    ]}
                />
            )}
            {section.type === 'process' && (
                <ArrayEditor
                    label="Koraci procesa"
                    items={Array.isArray(d.steps) ? d.steps : []}
                    onChange={(items) => onChange({ steps: items })}
                    blank={{ number: '', title: '', text: '' }}
                    fields={[
                        { key: 'number', label: 'Broj (npr. 01)', required: false, width: 'narrow' },
                        { key: 'title', label: 'Naslov *', required: true },
                        { key: 'text', label: 'Opis', required: false, multiline: true },
                    ]}
                />
            )}
            {section.type === 'faq' && (
                <ArrayEditor
                    label="Pitanja i odgovori"
                    items={Array.isArray(d.items) ? d.items : []}
                    onChange={(items) => onChange({ items: items })}
                    blank={{ question: '', answer: '' }}
                    fields={[
                        { key: 'question', label: 'Pitanje *', required: true },
                        { key: 'answer', label: 'Odgovor *', required: true, multiline: true },
                    ]}
                />
            )}
            {section.type === 'why_us' && (
                <ArrayEditor
                    label="Stavke / značajke"
                    items={Array.isArray(d.items) ? d.items : []}
                    onChange={(items) => onChange({ items: items })}
                    blank={{ icon: 'check-circle', title: '', text: '' }}
                    fields={[
                        { key: 'icon', label: 'Ikona (npr. award, clock, shield-check)', required: false, width: 'narrow' },
                        { key: 'title', label: 'Naslov *', required: true },
                        { key: 'text', label: 'Opis', required: false, multiline: true },
                    ]}
                />
            )}
            {section.type === 'trust' && (
                <ArrayEditor
                    label="Trust signali"
                    items={Array.isArray(d.items) ? d.items : []}
                    onChange={(items) => onChange({ items: items })}
                    blank={{ icon: 'check-circle', text: '' }}
                    fields={[
                        { key: 'icon', label: 'Ikona', required: false, width: 'narrow' },
                        { key: 'text', label: 'Tekst *', required: true },
                    ]}
                />
            )}
            {section.type === 'hero' && (
                <ArrayEditor
                    label="Trust signali (unutar Hero sekcije)"
                    items={Array.isArray(d.trust) ? d.trust : []}
                    onChange={(items) => onChange({ trust: items })}
                    blank={{ icon: 'check-circle', text: '' }}
                    fields={[
                        { key: 'icon', label: 'Ikona', required: false, width: 'narrow' },
                        { key: 'text', label: 'Tekst *', required: true },
                    ]}
                />
            )}
            <details className="text-white/70">
                <summary className="text-xs cursor-pointer hover:text-white">Napredne postavke (JSON / Custom CSS)</summary>
                <div className="mt-3 space-y-3">
                    <div>
                        <Label className="text-white text-xs">Cijela data (JSON)</Label>
                        <Textarea value={JSON.stringify(d, null, 2)} onChange={(e) => { try { const v = JSON.parse(e.target.value); onChange(v); } catch {} }} rows={8} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white font-mono text-xs" />
                    </div>
                    <div>
                        <Label className="text-white text-xs">Custom CSS (samo za ovu sekciju)</Label>
                        <Textarea value={section.custom_css || ''} onChange={(e) => onCssChange(e.target.value)} rows={4} className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white font-mono text-xs" />
                    </div>
                </div>
            </details>
        </div>
    );
}

// Minimal array editor: + Add / Trash per row. No drag-drop, no preview, no upload widget.
function ArrayEditor({ label, items, fields, blank, onChange }) {
    const update = (i, key, val) => {
        const next = [...items];
        next[i] = { ...(next[i] || {}), [key]: val };
        onChange(next);
    };
    const add = () => onChange([...(items || []), { ...blank }]);
    const remove = (i) => {
        if (!confirm('Obrisati ovu stavku?')) return;
        const next = items.filter((_, idx) => idx !== i);
        onChange(next);
    };
    return (
        <div className="rounded-lg border border-[#232A36] p-3 bg-[#0F1218]" data-testid={`array-editor-${label.toLowerCase().replace(/\s/g, '-')}`}>
            <div className="flex items-center justify-between mb-2">
                <Label className="text-white text-xs">{label} ({(items || []).length})</Label>
                <Button size="sm" variant="outline" onClick={add} className="border-[#232A36] text-white hover:bg-[#232A36] h-7 text-xs" data-testid="array-editor-add-item">
                    <Plus className="mr-1 h-3 w-3" /> Dodaj stavku
                </Button>
            </div>
            {(!items || items.length === 0) ? (
                <p className="text-xs text-white/45 italic px-1 py-2">Nema stavki. Kliknite “+ Dodaj stavku”.</p>
            ) : (
                <div className="space-y-2">
                    {items.map((it, i) => (
                        <div key={i} className="rounded-md border border-[#232A36] p-2 bg-[#0B0C0E]">
                            <div className="flex items-center justify-between mb-2">
                                <span className="text-[10px] uppercase tracking-[0.18em] text-white/45">#{i + 1}</span>
                                <Button size="icon" variant="outline" onClick={() => remove(i)} className="h-7 w-7 border-red-900 text-red-400 hover:bg-red-900/20" data-testid="array-editor-remove-item">
                                    <Trash2 className="h-3 w-3" />
                                </Button>
                            </div>
                            <div className="space-y-2">
                                {fields.map((f) => (
                                    <div key={f.key}>
                                        <Label className="text-white/80 text-[11px]">{f.label}</Label>
                                        {f.multiline ? (
                                            <Textarea
                                                value={(it || {})[f.key] || ''}
                                                onChange={(e) => update(i, f.key, e.target.value)}
                                                rows={2}
                                                className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white text-xs"
                                                placeholder={f.placeholder || ''}
                                            />
                                        ) : (
                                            <Input
                                                value={(it || {})[f.key] || ''}
                                                onChange={(e) => update(i, f.key, e.target.value)}
                                                className="mt-1 bg-[#0B0C0E] border-[#232A36] text-white h-8 text-xs"
                                                placeholder={f.placeholder || ''}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}
